import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Collapse,
  Card,
  CardBody,
  ListGroup,
  ListGroupItem,
} from "reactstrap";
import AppNavBar from "../common/AppNavBar";
import { Link } from "react-router-dom";
import logo from "../assets/img/boxs_logo_new_white.png";

const itJustWorksItems = [
  {
    title: "I don't know how to take measurements.",
    icon: "fa-solid fa-screwdriver-wrench",
    response:
      "Don't worry — we guide you through every step. Plus, our optional site validation ensures everything fits perfectly before production begins.",
  },
  {
    title: "I don't have much build experience.",
    icon: "fa-solid fa-book",
    response:
      "You don't need it. Just place your order like you're selecting products. We turn your inputs into plug-and-play modular units — no cutlists, no confusion.",
  },
  {
    title: "I work with a local carpenter — will this even work for us?",
    icon: "fa-solid fa-user-group",
    response:
      "Absolutely. Every cabinet comes pre-packed, labeled, and ready to assemble. Your carpenter will appreciate how easy and precise it is to put together.",
  },
  {
    title: "Can I order from Boxs as a homeowner?",
    icon: "fa-solid fa-user-group",
    response:
      "Boxs is built exclusively for architects, designers, and professionals - strictly B2B by design. If you're a homeowner, we're happy to work through your designer to ensure the best experience.",
  },
];

const testimonials = [
  {
    quote:
      "After using the Boxs app, our project timelines have reduced a lot. Earlier, we had frequent mistakes with manual cutlists and separate 3D/2D files. With Boxs, the combined 2D and 3D visuals help us instantly catch and fix errors, which also corrects the cutlist automatically. It saves time, manpower, and cost. Even during installation, labeled panels and clear packaging make panel search and separation easy — reducing labour time significantly.",
    name: "Ar.Monisha",
    role: "Verdant Architects, Tiruppur.",
  },
  {
    quote:
      "Boxs is a thoughtful startup that truly bridges the gaps in interior project deliverables. As a designer, they've taken care of everything that used to be tiring and tedious — from their smart shopping cart-style ordering to professional, on-time delivery. The entire experience was seamless and handled with care. Team Boxs is humble, customer-friendly, and genuinely supportive throughout. I highly recommend Boxs to anyone in the design space!",
    name: "Jeyachandran",
    role: "Habitro Interiors, Chennai",
  },
  {
    quote:
      "I was looking for an end-to-end modular solution and after trying other platforms, Boxs made my workflow truly hassle-free. I no longer deal with material procurement, and coordination is smooth. Their team listens, improves based on feedback, and offers a complete solution for modular firms. It's helped me focus more on business than backend chaos. I'd confidently rate Boxs 8 to 9 out of 10.",
    name: "Ar. Balaguru",
    role: "Home Frame Interiors, Trichy",
  },
];

const commonQuestions = [
  {
    question: "How is Boxs different from a local modular vendor?",
    answer:
      "Boxs gives you a self-serve, tech-driven way to order customizable modules — no follow-ups, no miscommunication. Your designs turn into factory-assembled, labeled cabinets delivered in 7 days with zero guesswork.",
  },
  {
    question: "Can I customize the size, material, or hardware?",
    answer:
      "Yes, everything is fully customizable — size, finish, carcass and shutter material, colors, and hardware. Boxs supports over 30 laminate options and multiple hardware brands.",
  },
  {
    question: "What if I'm unsure about measurements?",
    answer:
      "We guide you through taking measurements accurately, and also offer a site validation service to double-check your inputs before production.",
  },
  {
    question: "Do I need trained labor to assemble Boxs modules?",
    answer:
      "No special training needed. Every module is packed and labeled per cabinet, with clear install drawings. Any local carpenter can easily assemble it.",
  },
  {
    question: "Where do you deliver?",
    answer:
      "We currently deliver across Tamil Nadu and Karnataka, with expansion plans in place. If you're outside these zones, get in touch and we'll do our best to assist.",
  },
];

const customizeOptions = [
  {
    icon: "fa-solid fa-ruler",
    title: "Size",
    desc: "Precise measurements that fit your exact specifications down to the millimeter.",
  },
  {
    icon: "fa-solid fa-box",
    title: "Material",
    desc: "Choose from premium materials that match your project's aesthetics and budget.",
  },
  {
    icon: "fa-solid fa-palette",
    title: "Finish",
    desc: "Select perfect finishes that complement your design vision.",
  },
  {
    icon: "fa-solid fa-screwdriver-wrench",
    title: "Hardware",
    desc: "Customize with quality hardware that ensures durability and function.",
  },
];

const featureSections = [
  {
    title: "Anyone In Your Team Can Order",
    content:
      "Boxs is simple enough for anyone to use — no technical drawings, no vendor calls. Just log in, customize, and place the order. Designers, project managers, or even interns can get it done.",
    image: "easy_to_use.png",
    keyPoints: [
      "🧑‍💼 No Training Needed",
      "🖱️ Self-Serve Workflow",
      "⚡ Fast & Intuitive",
      "🔄 Zero Back-and-Forth",
    ],
  },
  {
    title: "Fully Customizable Modules to Realize Any Design",
    content:
      "Every module is made to match your exact design — size, material, finish, and hardware. Build anything you've drawn, down to the last detail, without needing to compromise on intent or quality.",
    image: "customizable.png",
    keyPoints: [
      "📏 Any Size",
      "🪵 Any Material",
      "🎨 Any Finish",
      "⚙️ Any Hardware",
    ],
  },
  {
    title: "Module Level Packing",
    content:
      "We pack and label modules individually — not just a pile of parts. Each packet is grouped by cabinet, making it easy to identify, track, and assemble with no missing pieces or confusion.",
    image: "packing.png",
    keyPoints: [
      "📦 Packed by Cabinet",
      "🏷️ Labeled Panels",
      "🛠️ Assembly-Ready",
      "🚫 No Mix-ups",
    ],
  },
  {
    title: "On-time Delivery",
    content:
      "Your order is delivered within 7 days — no delays, no excuses. Our automated manufacturing and streamlined logistics ensure predictable timelines that help you plan your projects without disruption.",
    image: "on_time.png",
    keyPoints: [
      "🗓️ 7-Day Dispatch",
      "🚫 No Delays",
      "⏰ Always On Time",
      "📍 Trackable Delivery",
    ],
  },
  {
    title: "Carpenter Friendly",
    content:
      "Clear install drawings and pre-labeled parts mean any carpenter can assemble Boxs modules with confidence. No special training, no on-site improvisation — just plug, fix, and finish.",
    image: "carpenter_friendly.png",
    keyPoints: [
      "📐 Install Drawings",
      "🏷️ Labeled Parts",
      "🔧 Hassle-Free Fit",
      "❓ No Guesswork",
    ],
  },
];

function HomePage() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [questionsOpen, setQuestionsOpen] = useState(null);

  return (
    <div className="home-page bg-dark text-light">
      <AppNavBar />
      {/* Hero */}
      <section
        className="home-bg-gradient-1 pb-4 pb-md-5 text-center"
        style={{ paddingTop: "150px" }}
      >
        <Container>
          <h1
            className="fw-bold mb-3 fs-35px fs-md-50px"
            style={{ color: "#cb9b7d" }}
          >
            Amazon for Modular Cabinets.
          </h1>
          <h4 className="fw-bold  mb-2 text-white lh-base fs-20px fs-md-30px">
            100% Customizable. Delivered in 7 Days.
          </h4>
          <p className="text-light mb-5 fs-md-20px fs-16px">
            For Architects & Designers.
          </p>
          <div className="d-flex justify-content-center gap-3 flex-wrap mb-5">
            <Link to="/catalog-collection?src=home_browse_catalog">
              <Button color="light" className="px-sm-5 py-sm-3 fs-6">
                <i className="fa-solid fa-book-open me-2"></i> Browse Catalog
              </Button>
            </Link>
            <Link to="/register?src=home_try_now">
              <Button color="light" outline className="px-sm-5 py-sm-3 fs-6">
                <i className="fa-solid fa-rocket me-2"></i> Try Now
              </Button>
            </Link>
          </div>
          <img
            src="https://app.boxs.in/hero_image.png"
            alt="Hero"
            className="img-fluid rounded-5 shadow"
          />
        </Container>
      </section>
      {/* Customize Everything */}
      <section className="home-bg-gradient-2 py-3">
        <Container>
          <h2 className="text-uppercase mb-2 fw-bold fs-md-50px fs-28px text-center">
            <span style={{ color: "#cb9b7d" }}>Customize.</span>{" "}
            <span className="text-AAAAAA">Everything.</span>
          </h2>
          <Row className="gy-3 justify-content-center">
            {customizeOptions.map((item, i) => (
              <Col key={i} xs="12" md="6" lg="3">
                <div className="h-100 p-4 rounded-3 text-start bg-black bg-opacity-25">
                  <div className="d-flex align-items-center mb-3 gap-2">
                    <i
                      className={`${item.icon}`}
                      style={{ fontSize: "1.5rem", color: "white" }}
                    ></i>
                    <h5 className="mb-0 fw-bold text-white fs-6">
                      {item.title}
                    </h5>
                  </div>
                  <p className="mb-0 text-white fs-6">{item.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
          <div className="d-flex justify-content-center mt-5">
            <Link to="/catalog-collection?src=home_customize">
              <Button color="light" className="mb-5 px-5 py-3 fs-6">
                <i className="fa-solid fa-sliders me-2"></i> Customize a Sample
                Cabinet
              </Button>
            </Link>
          </div>
        </Container>
      </section>
      {/* Features */}
      {featureSections.map((section, i) => (
        <section className={`home-bg-gradient-${i + 3} py-3 py-xl-5`} key={i}>
          <Container>
            <Row className="align-items-stretch gx-3 gx-xl-5">
              <Col md="6" className={i % 2 === 0 ? "" : "order-md-2"}>
                <img
                  src={`./why_boxs/${section.image}`}
                  alt={section.title}
                  className="img-fluid rounded-5 shadow mb-4 mb-md-0"
                />
              </Col>
              <Col
                md="6"
                className="d-flex flex-column justify-content-between text-center text-xl-start"
              >
                <div>
                  <h2
                    className="fw-bold mb-3 fs-md-50px fs-28px pt-xl-5"
                    style={{
                      color: "#cb9b7d",
                    }}
                  >
                    {section.title}
                  </h2>
                  <p className="text-light fs-md-20px fs-16px">
                    {section.content}
                  </p>
                </div>
                <div className="row row-cols-2 gx-4 gy-3 mt-4 pb-xl-5">
                  {section.keyPoints.map((point, pointIndex) => (
                    <div
                      className="d-flex align-items-center gap-2 text-AAAAAA"
                      style={{ fontSize: "1rem" }}
                      key={pointIndex}
                    >
                      {/* <i
                        className="fa-regular fa-note-sticky"
                        style={{ fontSize: "1.25rem" }}
                      ></i> */}
                      <span className="fw-semibold fs-md-20px">{point}</span>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
            <hr className="w-75 mt-5 mx-auto d-xl-none" />
          </Container>
        </section>
      ))}
      {/* It Just Works */}
      <section className="py-3 home-bg-gradient-8">
        <Container>
          <h2
            className="text-center fw-bold mb-3 fs-md-50px fs-28px"
            style={{ color: "#cb9b7d" }}
          >
            It Just Works.
          </h2>
          <p className="text-center mb-4 text-AAAAAA fs-5">
            Yes. Here's what other professionals discovered.
          </p>
          <Row className="align-items-center">
            <Col md="5">
              <ListGroup className="gap-3 border-0">
                {itJustWorksItems.map((item, index) => (
                  <ListGroupItem
                    key={index}
                    onClick={() => setActiveIndex(index)}
                    className={`d-flex align-items-center gap-3 py-3 px-4 rounded transition-all ${
                      activeIndex === index
                        ? "bg-dark border border-light"
                        : "bg-transparent border-0"
                    }`}
                    style={{ cursor: "pointer" }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center rounded"
                      style={{
                        width: 32,
                        height: 32,
                        backgroundColor:
                          activeIndex === index ? "#8b5cf6" : "#1e1e1e",
                      }}
                    >
                      <i
                        className={item.icon}
                        style={{
                          color: activeIndex === index ? "white" : "#666",
                        }}
                      ></i>
                    </div>
                    <span className="fw-medium text-light fs-6">
                      {item.title}
                    </span>
                  </ListGroupItem>
                ))}
              </ListGroup>
            </Col>
            <Col md="7">
              <div
                className="mt-4 mt-xl-0 p-4 border rounded bg-black"
                style={{
                  minHeight: "130px",
                }}
              >
                <p className="text-AAAAAA fs-6 m-0">
                  {itJustWorksItems[activeIndex].response}
                </p>
              </div>
            </Col>
          </Row>
          <div className="d-flex justify-content-center mt-5">
            <Link to="/sample-projects?src=home_view_sample">
              <Button color="light" className="mb-5 px-5 py-3 fs-6">
                <i className="fa-solid fa-bars me-2"></i> View Sample Orders
              </Button>
            </Link>
          </div>
        </Container>
      </section>
      {/* FAQ */}
      <section id="faq" className="py-3 p-xl-5 text-center home-bg-gradient-9">
        <Container>
          <h2
            className="text-center fw-bold mb-3 fs-md-50px fs-28px"
            style={{ color: "#cb9b7d" }}
          >
            Common Questions
          </h2>
          <div className="text-start mx-auto" style={{ maxWidth: "600px" }}>
            {commonQuestions.map(({ question, answer }, i) => (
              <Card
                key={i}
                className="mb-3 bg-dark border-secondary text-light"
              >
                <Button
                  color="black"
                  className="text-start w-100 text-light fw-semibold fs-6"
                  onClick={() =>
                    setQuestionsOpen(questionsOpen === i ? null : i)
                  }
                >
                  {question}
                </Button>
                <Collapse isOpen={questionsOpen === i}>
                  <CardBody className="text-secondary fs-13px">
                    {answer}
                  </CardBody>
                </Collapse>
              </Card>
            ))}
          </div>
          <div className="mt-5 d-flex justify-content-center gap-3 flex-wrap">
            <Link
              to="https://wa.me/7305972920?text=Tell%20me%20more%20about%20Boxs"
              className="text-decoration-none"
              target="_blank"
              onClick={() => trackGAEvent("home_faq_talk_to_us")}
            >
              <Button
                color="light"
                className="px-sm-5 py-sm-3 rounded-3 d-flex align-items-center gap-2 fs-6"
              >
                <i className="fa-solid fa-phone"></i> Talk to Us
              </Button>
            </Link>
            <Link
              to="https://wa.me/7305972920?text=Tell%20me%20more%20about%20Boxs"
              className="text-decoration-none"
              target="_blank"
              onClick={() => trackGAEvent("home_faq_chat_with_us")}
            >
              <Button
                outline
                color="light"
                className="px-sm-5 py-sm-3 rounded-3 d-flex align-items-center gap-2 fs-6"
              >
                <i className="fa-regular fa-message"></i> Chat with Us
              </Button>
            </Link>
          </div>
        </Container>
      </section>
      {/* Testimonials */}
      <section className="py-3 p-xl-5 home-bg-gradient-10 text-light">
        <Container>
          <h2
            className="text-center fw-bold mb-3 fs-md-50px fs-25px"
            style={{ color: "#cb9b7d" }}
          >
            Architects & Designers love Boxs!
          </h2>
          <Row className="justify-content-center">
            {testimonials.map((t, i) => (
              <Col md="5" className="mb-4" key={i}>
                <Card className="bg-black text-light border-0 p-4 h-100">
                  <CardBody>
                    <p className="mb-3 fs-14px text-secondary">{t.quote}</p>
                    <strong>{t.name}</strong>
                    <div className="small text-secondary">{t.role}</div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>
      {/* Still Got Questions CTA */}
      <section className="text-center py-5 p-xl-5 home-bg-gradient-11 border-top border-secondary">
        <Container>
          <h2
            className="fw-bold mb-3 fs-md-50px fs-25px"
            style={{ color: "#cb9b7d" }}
          >
            Still Got Questions?
          </h2>
          <p className="text-light fs-6 mb-4">
            Talk to us. We're ready to help you with any questions about our
            products and services.
          </p>
          <div className="d-flex justify-content-center gap-3 flex-wrap">
            <Link
              to="https://wa.me/7305972920?text=Tell%20me%20more%20about%20Boxs"
              className="text-decoration-none"
              onClick={() => trackGAEvent("home_questions_chat_with_us")}
              target="_blank"
            >
              <Button
                color="light"
                className="px-sm-5 py-sm-3 rounded-3 d-flex align-items-center gap-2 fs-6"
              >
                <i className="fa-regular fa-message"></i> Chat with Us
              </Button>
            </Link>
            <Link
              to="https://wa.me/7305972920?text=Tell%20me%20more%20about%20Boxs"
              className="text-decoration-none"
              onClick={() => trackGAEvent("home_questions_schedule_a_call")}
              target="_blank"
            >
              <Button
                outline
                color="light"
                className="px-sm-5 py-sm-3 rounded-3 d-flex align-items-center gap-2 fs-6"
              >
                <i className="fa-solid fa-phone"></i> Schedule a Call
              </Button>
            </Link>
          </div>
        </Container>
      </section>
      {/* Footer */}
      <footer className="text-light py-2 home-bg-gradient-12 border-top border-secondary">
        <Container>
          <Row className="gy-4">
            <Col md="3">
              <div className="d-flex align-items-center mb-2">
                <img src={logo} alt="Boxs Logo" height={24} className="me-2" />
              </div>
              <p className="fs-6 text-secondary">
                Customizable interior modules for architects and interior
                designers.
              </p>
            </Col>
            <Col md="3">
              <div className="fw-semibold mb-2 fs-6">Quick Links</div>
              <ul className="list-unstyled fs-6">
                <li>
                  <a
                    href="#about"
                    className="text-secondary text-decoration-none"
                  >
                    About
                  </a>
                </li>
                <li>
                  <a
                    href="#faq"
                    className="text-secondary text-decoration-none"
                  >
                    FAQs
                  </a>
                </li>
                <li>
                  <a href="#" className="text-secondary text-decoration-none">
                    Catalog
                  </a>
                </li>
                <li>
                  <a href="#" className="text-secondary text-decoration-none">
                    Sample Orders
                  </a>
                </li>
              </ul>
            </Col>
            <Col md="3">
              <div className="fw-semibold mb-2 fs-6">B2B Only</div>
              <p className="text-secondary fs-6">
                Currently delivering in Tamil Nadu & Karnataka
              </p>
            </Col>
            <Col md="3">
              <div className="fw-semibold mb-2 fs-6">Contact</div>
              <p className="fs-6 text-secondary mb-1">
                115, Lakshmanaswamy Salai,
                <br />
                K. K. Nagar,
                <br />
                Chennai - 600078
              </p>
              <a
                href="mailto:hello@boxs.in"
                className="small text-secondary fs-12px"
              >
                hello@boxs.in
              </a>
              <p className="small text-secondary mt-1">
                B2B only. Currently delivering in Tamil Nadu & Karnataka
              </p>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
}

export default HomePage;
